import { useState } from 'react';

const FORM_URL = 'https://readdy.ai/api/form/d6ob9t130mftb86r3hk0';

const gradeOptions = [
  'Kindergarten', 'Grade 1', 'Grade 2', 'Grade 3', 'Grade 4',
  'Grade 5', 'Grade 6', 'Grade 7', 'Grade 8',
  'Grade 9 – Algebra 1', 'Grade 10 – Geometry',
  'Grade 11 – Algebra 2 & Trig', 'Grade 12 – Pre-Calculus / Calculus',
  'SAT Prep', 'ACT Prep', 'GRE Prep', 'GED Prep', 'SHSAT Prep',
  'AP Calculus', 'HESI / TEAS', 'Other',
];

const formatOptions = [
  { value: 'online', label: 'Online (Zoom / Google Meet)', icon: 'ri-computer-line' },
  { value: 'face-to-face', label: 'Face-to-Face (NYC / NJ)', icon: 'ri-user-heart-line' },
  { value: 'either', label: 'Either Works for Me', icon: 'ri-shake-hands-line' },
];

export default function BookTrialCTA() {
  const [showForm, setShowForm] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [charCount, setCharCount] = useState(0);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const message = (form.elements.namedItem('message') as HTMLTextAreaElement)?.value || '';
    if (message.length > 500) return;

    setSubmitting(true);
    const data = new URLSearchParams();
    Array.from(new FormData(form)).forEach(([key, value]) => {
      data.append(key, value as string);
    });

    try {
      await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
      setSubmitted(true);
    } catch {
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="book-trial" className="py-20 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/4 w-96 h-96 rounded-full opacity-10"
          style={{ background: 'radial-gradient(circle, #FF6B35 0%, transparent 70%)' }}></div>
        <div className="absolute bottom-0 right-1/4 w-96 h-96 rounded-full opacity-10"
          style={{ background: 'radial-gradient(circle, #FF6B35 0%, transparent 70%)' }}></div>
      </div>

      <div className="max-w-5xl mx-auto px-6 relative z-10">

        {!showForm ? (
          /* ── Hero CTA ── */
          <div className="text-center text-white">
            <div className="inline-flex items-center gap-2 px-5 py-2 bg-orange-600/20 border border-orange-500/40 rounded-full text-orange-300 text-sm font-bold mb-8 tracking-wide">
              <i className="ri-calendar-check-line"></i>
              LIMITED SPOTS AVAILABLE EACH WEEK
            </div>

            <h2 className="text-5xl lg:text-6xl font-bold mb-6 leading-tight">
              Book Your{' '}
              <span className="text-orange-400">Free Trial Lesson</span>
              <br />with Mr. Augustine
            </h2>

            <p className="text-xl text-gray-300 max-w-2xl mx-auto mb-10 leading-relaxed">
              Experience the teaching that has transformed thousands of students — completely free, no commitment required. One session could change everything.
            </p>

            {/* Trust signals */}
            <div className="flex flex-wrap items-center justify-center gap-6 mb-12">
              {[
                { icon: 'ri-time-line', text: '60-Minute Session' },
                { icon: 'ri-shield-check-line', text: 'No Credit Card Needed' },
                { icon: 'ri-star-fill', text: '4.9/5 Rating' },
                { icon: 'ri-global-line', text: 'Online & In-Person' },
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-2 text-gray-300 text-sm">
                  <div className="w-8 h-8 flex items-center justify-center bg-orange-600/20 rounded-lg">
                    <i className={`${item.icon} text-orange-400`}></i>
                  </div>
                  <span>{item.text}</span>
                </div>
              ))}
            </div>

            <button
              onClick={() => setShowForm(true)}
              className="inline-flex items-center gap-3 px-12 py-5 bg-orange-600 hover:bg-orange-500 text-white text-xl font-bold rounded-2xl transition-all hover:scale-105 shadow-2xl shadow-orange-900/50 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-calendar-line text-2xl"></i>
              Book My Free Trial Lesson
              <i className="ri-arrow-right-line text-2xl"></i>
            </button>

            <p className="text-gray-500 text-sm mt-5 mb-14">
              Join 5,000+ students who started exactly this way
            </p>

            {/* ── Membership Plan Nudge ── */}
            <div className="border-t border-white/10 pt-12">
              <p className="text-gray-400 text-sm font-semibold uppercase tracking-widest mb-3">
                Or skip the trial — jump straight in
              </p>
              <h3 className="text-3xl font-bold text-white mb-2">
                Pick a Plan &amp; Get <span className="text-orange-400">Instant Access</span>
              </h3>
              <p className="text-gray-400 max-w-xl mx-auto mb-8 text-sm leading-relaxed">
                Every plan comes with Mr. Augustine's proven materials — the same methods that turned failing students into top performers. Your success is guaranteed or your money back.
              </p>

              <div className="grid md:grid-cols-3 gap-5 max-w-3xl mx-auto mb-8">
                {[
                  {
                    plan: 'Free Trial',
                    price: '$0',
                    duration: '14 days',
                    perks: ['1 grade level', '5 practice tests', 'Basic progress tracking'],
                    icon: 'ri-gift-line',
                    style: 'border-white/20 bg-white/5',
                    btnStyle: 'border-2 border-white/30 text-white hover:bg-white/10',
                  },
                  {
                    plan: 'Standard',
                    price: '$29.99',
                    duration: '/month',
                    perks: ['All grades K–12', 'Unlimited lessons & tests', '2 test preps + worksheets'],
                    icon: 'ri-star-fill',
                    style: 'border-orange-500 bg-orange-600/15',
                    btnStyle: 'bg-orange-600 hover:bg-orange-500 text-white',
                    badge: 'MOST POPULAR',
                  },
                  {
                    plan: 'All Access',
                    price: '$49.99',
                    duration: '/month',
                    perks: ['Everything in Standard', '1-on-1 with Mr. Augustine', 'All test preps + live classes'],
                    icon: 'ri-vip-crown-line',
                    style: 'border-amber-400/60 bg-amber-500/10',
                    btnStyle: 'bg-amber-500 hover:bg-amber-400 text-white',
                  },
                ].map((p, i) => (
                  <div key={i} className={`relative border-2 rounded-2xl p-6 text-left flex flex-col ${p.style}`}>
                    {p.badge && (
                      <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-orange-600 text-white text-xs font-bold px-3 py-1 rounded-full whitespace-nowrap">
                        {p.badge}
                      </div>
                    )}
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-8 h-8 flex items-center justify-center">
                        <i className={`${p.icon} text-orange-400 text-xl`}></i>
                      </div>
                      <span className="font-bold text-white">{p.plan}</span>
                    </div>
                    <div className="text-3xl font-bold text-white mb-1">
                      {p.price}<span className="text-sm font-normal text-gray-400">{p.duration}</span>
                    </div>
                    <ul className="mt-3 mb-5 space-y-2 flex-1">
                      {p.perks.map((perk, j) => (
                        <li key={j} className="flex items-start gap-2 text-gray-300 text-xs">
                          <i className="ri-check-line text-orange-400 mt-0.5 flex-shrink-0"></i>
                          {perk}
                        </li>
                      ))}
                    </ul>
                    <a
                      href="#pricing"
                      className={`w-full py-2.5 rounded-xl font-bold text-sm text-center transition-all whitespace-nowrap inline-block ${p.btnStyle}`}
                    >
                      Choose {p.plan}
                    </a>
                  </div>
                ))}
              </div>

              <p className="text-gray-600 text-xs">
                <i className="ri-shield-check-line text-green-500 mr-1"></i>
                30-day money-back guarantee · Cancel anytime · Instant access to all materials
              </p>
            </div>
          </div>
        ) : submitted ? (
          /* ── Success State ── */
          <div className="text-center text-white py-12">
            <div className="w-24 h-24 flex items-center justify-center bg-green-500/20 border-2 border-green-400 rounded-full mx-auto mb-6">
              <i className="ri-check-line text-5xl text-green-400"></i>
            </div>
            <h3 className="text-4xl font-bold mb-4">You're Booked! 🎉</h3>
            <p className="text-xl text-gray-300 max-w-xl mx-auto mb-8">
              Thank you! Mr. Augustine's team will reach out within <strong className="text-orange-400">24 hours</strong> to confirm your free trial lesson time.
            </p>
            <div className="inline-flex items-center gap-2 px-6 py-3 bg-orange-600/20 border border-orange-500/40 rounded-xl text-orange-300 text-sm">
              <i className="ri-mail-line"></i>
              Check your email for a confirmation message
            </div>
          </div>
        ) : (
          /* ── Booking Form ── */
          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-3xl p-8 lg:p-12">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h3 className="text-3xl font-bold text-white">Book Your Free Trial</h3>
                <p className="text-gray-400 mt-1">Fill in your details and we'll confirm within 24 hours</p>
              </div>
              <button
                onClick={() => setShowForm(false)}
                className="w-10 h-10 flex items-center justify-center text-gray-400 hover:text-white hover:bg-white/10 rounded-xl transition-all cursor-pointer"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>

            <form
              id="book-trial-form"
              data-readdy-form
              onSubmit={handleSubmit}
              className="space-y-6"
            >
              {/* Name + Email */}
              <div className="grid md:grid-cols-2 gap-5">
                <div>
                  <label className="block text-sm font-semibold text-gray-300 mb-2">
                    Full Name <span className="text-orange-400">*</span>
                  </label>
                  <input
                    type="text"
                    name="full_name"
                    required
                    placeholder="e.g. Maria Gonzalez"
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-gray-500 text-sm focus:outline-none focus:border-orange-500 transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-300 mb-2">
                    Email Address <span className="text-orange-400">*</span>
                  </label>
                  <input
                    type="email"
                    name="email"
                    required
                    placeholder="you@example.com"
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-gray-500 text-sm focus:outline-none focus:border-orange-500 transition-colors"
                  />
                </div>
              </div>

              {/* Phone + Grade */}
              <div className="grid md:grid-cols-2 gap-5">
                <div>
                  <label className="block text-sm font-semibold text-gray-300 mb-2">
                    Phone Number <span className="text-orange-400">*</span>
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    required
                    placeholder="(555) 000-0000"
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-gray-500 text-sm focus:outline-none focus:border-orange-500 transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-300 mb-2">
                    Grade Level / Subject <span className="text-orange-400">*</span>
                  </label>
                  <select
                    name="grade_level"
                    required
                    defaultValue=""
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white text-sm focus:outline-none focus:border-orange-500 transition-colors cursor-pointer appearance-none"
                    style={{ colorScheme: 'dark' }}
                  >
                    <option value="" disabled>Select grade / subject</option>
                    {gradeOptions.map(g => (
                      <option key={g} value={g} className="bg-slate-800 text-white">{g}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Format */}
              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-3">
                  Preferred Format <span className="text-orange-400">*</span>
                </label>
                <div className="grid grid-cols-3 gap-3">
                  {formatOptions.map(opt => (
                    <label key={opt.value} className="cursor-pointer">
                      <input type="radio" name="format" value={opt.label} required className="sr-only peer" />
                      <div className="flex flex-col items-center gap-2 p-4 bg-white/5 border-2 border-white/10 rounded-xl text-center text-sm text-gray-400 peer-checked:border-orange-500 peer-checked:bg-orange-600/10 peer-checked:text-orange-300 transition-all hover:border-white/30">
                        <div className="w-8 h-8 flex items-center justify-center">
                          <i className={`${opt.icon} text-xl`}></i>
                        </div>
                        <span className="leading-tight">{opt.label}</span>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              {/* Message */}
              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-2">
                  Tell Us About Your Struggles (Optional)
                </label>
                <textarea
                  name="message"
                  rows={3}
                  maxLength={500}
                  placeholder="e.g. My daughter is in Grade 7 and struggling with fractions and word problems..."
                  onChange={e => setCharCount(e.target.value.length)}
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-gray-500 text-sm focus:outline-none focus:border-orange-500 transition-colors resize-none"
                />
                <div className={`text-right text-xs mt-1 ${charCount > 480 ? 'text-orange-400' : 'text-gray-500'}`}>
                  {charCount}/500
                </div>
              </div>

              {/* Submit */}
              <button
                type="submit"
                disabled={submitting}
                className="w-full py-4 bg-orange-600 hover:bg-orange-500 disabled:opacity-60 text-white text-lg font-bold rounded-xl transition-all hover:scale-[1.02] shadow-lg shadow-orange-900/40 whitespace-nowrap cursor-pointer inline-flex items-center justify-center gap-3"
              >
                {submitting ? (
                  <>
                    <i className="ri-loader-4-line animate-spin text-xl"></i>
                    Sending Your Request...
                  </>
                ) : (
                  <>
                    <i className="ri-calendar-check-line text-xl"></i>
                    Confirm My Free Trial Lesson
                    <i className="ri-arrow-right-line text-xl"></i>
                  </>
                )}
              </button>

              <p className="text-center text-gray-500 text-xs">
                By submitting, you agree to be contacted by Mr. Augustine's team. No spam, ever.
              </p>
            </form>
          </div>
        )}
      </div>
    </section>
  );
}
